// This polyfill's IE toCanvas method
import 'blueimp-canvas-to-blob/js/canvas-to-blob.min';

import React, { PureComponent } from 'react';

import loadImage from 'blueimp-load-image';

import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import FormControl from '@material-ui/core/FormControl';
import Input from '@material-ui/core/Input';

import { ICON_NAMES, MaterialDesignIcon } from '../util/material-design-icons';

interface Props {
  onUpload: (result: {
    file: Blob | null;
    fullResFile: any | null;
    fileExtension: string | null;
  }) => void;
  fixedWidth: number;
  subtitle?: string;
  svgAllowed?: boolean;
  selectedImageUrl?: string | null;
  onUseNewImage?: () => void;
}

type State = {
  imageErrorMessage: string | null;
  imageUrl: string | null;
  processing: boolean;
};

const defaultState = {
  imageErrorMessage: null,
  imageUrl: null,
  processing: false,
};

const MAX_IMAGE_SIZE_IN_BYTES = 2097152; // 2MB

export class CustomerImageEditor extends PureComponent<Props, State> {
  static convertCanvasToBlob = (canvas: HTMLCanvasElement) =>
    new Promise<Blob | null>((resolve) => {
      canvas.toBlob((blob) => resolve(blob ?? null));
    });

  state: State = defaultState;

  useNewImage = () => {
    const { onUseNewImage } = this.props;
    if (onUseNewImage) {
      onUseNewImage();
    }
    this.setState(defaultState);
    this.props.onUpload({
      file: null,
      fullResFile: null,
      fileExtension: null,
    });
  };

  // IF (02/19/20): Max width is set at 2x the fixed width to account for retina
  // displays showing crisp images @2x.
  handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const allowedFileTypes = [
      {
        mimeType: 'image/jpeg',
        extension: 'jpg',
      },
      {
        mimeType: 'image/png',
        extension: 'png',
      },
      {
        mimeType: 'image/webp',
        extension: 'webp',
      },
    ];

    if (this.props.svgAllowed) {
      allowedFileTypes.push({
        mimeType: 'image/svg+xml',
        extension: 'svg',
      });
    }

    const [originalFile] = (e.target as any).files as File[];

    this.setState({ processing: true });

    if (!allowedFileTypes.find((t) => t.mimeType === originalFile.type)) {
      this.setState({
        imageErrorMessage: `Invalid file type, must be an image (${allowedFileTypes
          .map((t) => t.extension)
          .join(', ')})`,
        processing: false,
      });
      return;
    }

    loadImage(originalFile, this.updateImagePreview(originalFile), {
      maxWidth: this.props.fixedWidth * 2,
      orientation: true,
      canvas: true,
    });
  };

  updateImagePreview = (originalFile: File) => async (canvas: any, data: any) => {
    const { fixedWidth } = this.props;

    if (data.originalWidth < fixedWidth) {
      this.setState({
        imageErrorMessage: `Image is too small, must be at least ${fixedWidth}px wide`,
        processing: false,
      });
      return;
    }

    // IF (02/20/20): If the original upload is larger than 2MB, we shrink it
    let fullResFile: File | Blob | null = originalFile;
    if (originalFile.size > MAX_IMAGE_SIZE_IN_BYTES) {
      const maxWidth = data.originalWidth * Math.sqrt(MAX_IMAGE_SIZE_IN_BYTES / originalFile.size);
      const scaledImage = (loadImage as any).scale(canvas, { maxWidth });
      fullResFile = await CustomerImageEditor.convertCanvasToBlob(scaledImage);
    }

    this.props.onUpload({
      file: await CustomerImageEditor.convertCanvasToBlob(canvas),
      fullResFile,
      fileExtension: originalFile.name.split('.').pop()?.toLowerCase() || null,
    });

    this.setState({
      imageUrl: canvas.toDataURL(),
      imageErrorMessage: '',
      processing: false,
    });
  };

  render() {
    const { subtitle, selectedImageUrl } = this.props;
    const { imageUrl, imageErrorMessage, processing } = this.state;

    if (processing) {
      return (
        <div className="typo-subtitle">
          <CircularProgress className="u-margin-right u-margin-left" size={20} />
          Processing...
        </div>
      );
    }

    return (
      <div>
        {imageUrl || selectedImageUrl ? (
          <>
            <div>
              <img
                style={{ maxWidth: '100%' }}
                src={imageUrl || selectedImageUrl || undefined}
                alt="Customer logo"
              />
              {imageErrorMessage && (
                <div className="typo-subtitle u-color-red">{imageErrorMessage}</div>
              )}
            </div>
            {!selectedImageUrl ? (
              <div>
                <Button variant="contained" size="small" onClick={this.useNewImage}>
                  <MaterialDesignIcon
                    iconName={ICON_NAMES.ADD_PHOTO_ALTERNATE}
                    className="u-margin-right-sm"
                  />
                  Use a new image
                </Button>
              </div>
            ) : null}
          </>
        ) : (
          <>
            <div className="typo-subtitle">{subtitle ?? 'Upload a new image'}</div>
            <FormControl margin="dense">
              <Input type="file" margin="dense" onChange={this.handleImageUpload} />
            </FormControl>
            {imageErrorMessage && (
              <div className="typo-subtitle u-color-red">{imageErrorMessage}</div>
            )}
          </>
        )}
      </div>
    );
  }
}
